import { dayKey } from "./series.js";
import { occurredAtMs } from "./signal-like.js";
// Provider-labelled name is the confident signal (Runna sends "Strength",
// Apple Health "Functional Strength Training", etc.). Substring matching, not
// word boundaries: compound names like "weightlifting" would slip past a
// trailing \b, and a workout name is not free prose where over-matching bites.
const STRENGTH_NAME_TOKENS = [
    "strength",
    "resistance",
    "weightlift",
    "weight lift",
    "weight train",
    "weights",
    "lifting",
    "powerlift",
    "crossfit",
    "calisthenic",
    "bodyweight",
    "body weight",
    // Unambiguous resistance equipment / lifts, so named lifts like "Barbell
    // Row" or "Cable Row" are credited as strength rather than falling to
    // "other". "cable row" stays a two-word token so a lone "cable" cannot match.
    "barbell",
    "dumbbell",
    "kettlebell",
    "deadlift",
    "cable row",
];
function nameHasToken(name, tokens) {
    const lower = name.toLowerCase();
    return tokens.some((token) => lower.includes(token));
}
// Terra's numeric activity-type enum: "Strength Training" is 80 (per Terra's
// ActivityType reference, https://docs.tryterra.co/reference/enums#activitytype).
// Providers' own strength variants (Apple's functional/traditional strength
// training, etc.) normalise into this code. Name matching stays the primary
// path; this catches sessions that arrive with a type but no free-text name.
const STRENGTH_ACTIVITY_TYPES = new Set([80]);
function asNumber(value) {
    return typeof value === "number" && Number.isFinite(value) ? value : null;
}
/**
 * How long a session ran, from whichever key wrote it. `activeSeconds` is the
 * shape every writer emits now; the other two are historical rows the manual
 * log and the seeds left behind, and guessing one spelling is what rendered a
 * blank on four surfaces against rows that had used another.
 */
export function workoutMinutes(payload) {
    const seconds = asNumber(payload.activeSeconds);
    const minutes = seconds !== null ? seconds / 60 : asNumber(payload.durationMinutes ?? payload.active_minutes);
    return minutes === null || minutes <= 0 ? null : Math.round(minutes);
}
/**
 * Moderate-to-vigorous minutes: what "active minutes" means to a member and
 * what the guideline counts. Deliberately not `activeSeconds`, which on a ring
 * is the whole day's movement, 264 minutes a day on real Oura rows against 39
 * of moderate-or-harder effort. Null when the provider sends no intensity
 * split, which is honest rather than wrong.
 */
export function moderateToVigorousMinutes(payload) {
    const moderate = asNumber(payload.moderateSeconds);
    const vigorous = asNumber(payload.vigorousSeconds);
    if (moderate === null && vigorous === null)
        return null;
    const seconds = (moderate ?? 0) + (vigorous ?? 0);
    return seconds <= 0 ? null : Math.round(seconds / 60);
}
function workoutLabel(name, category) {
    // Every caller renders this to a member, and Oura sends "walking" in lower
    // case. A name that already carries capitals is the provider's own wording
    // ("Functional Strength Training") and stays as written.
    if (name.length > 0) {
        return name === name.toLowerCase() ? name.charAt(0).toUpperCase() + name.slice(1) : name;
    }
    if (category === "strength")
        return "Strength session";
    return "Workout";
}
/** The agent-facing phrase for a category, or null when there is none to add. */
function workoutCategoryLabel(category) {
    return category === "strength" ? "strength training" : null;
}
/**
 * Classify a `wearable.workout` payload. Strength is asserted only from an
 * explicit name (Runna's "Strength", Apple's "Functional Strength Training",
 * "Barbell Row", etc.) or a verified activity type; everything else is "other".
 * We never infer strength structurally, and we never infer cardio at all: the
 * session name carries that, and guessing from locomotion mislabelled
 * yoga/mobility sessions that record incidental steps.
 */
export function classifyWorkout(payload) {
    // `activity` is what the manual log wrote before `activityName` was canonical.
    const raw = payload.activityName ?? payload.activity;
    const name = typeof raw === "string" ? raw.trim() : "";
    const activityType = asNumber(payload.activityType);
    const minutes = workoutMinutes(payload);
    const nameIsStrength = name.length > 0 && nameHasToken(name, STRENGTH_NAME_TOKENS);
    const typeIsStrength = activityType !== null && STRENGTH_ACTIVITY_TYPES.has(activityType);
    const isStrength = nameIsStrength || typeIsStrength;
    const category = isStrength ? "strength" : "other";
    const label = workoutLabel(name, category);
    return { category, isStrength, minutes, label };
}
/**
 * A one-line human summary of a session: its name, then its kind and duration
 * in parentheses when those are known, e.g. "Strength (strength training, 55
 * min)" or just "Morning walk" when neither is. Shared so the Brief formatter
 * and the Heartbeat prompt read a session the same way.
 */
export function describeWorkout(payload) {
    const { category, minutes, label } = classifyWorkout(payload);
    const kind = workoutCategoryLabel(category); // "strength training" | null
    const duration = minutes === null ? null : `${minutes} min`;
    const facets = [kind, duration].filter((facet) => facet !== null);
    if (facets.length === 0)
        return label;
    return `${label} (${facets.join(", ")})`;
}
/** A session as a member-facing label: "Hiking, 62 min", or just its name. */
export function workoutLabelWithMinutes(payload) {
    const { label, minutes } = classifyWorkout(payload);
    return minutes === null ? label : `${label}, ${minutes} min`;
}
/** True when a signal is a `wearable.workout` the classifier reads as strength. */
export function isStrengthWorkout(signal) {
    return signal.kind === "wearable.workout" && classifyWorkout(signal.payload).isStrength;
}
/**
 * When a session ended, in epoch ms: its start (`occurredAt`) plus its active
 * duration. `occurredAt` is the session start, so a caller gating "just
 * finished" copy on recency must add the duration, not read the start alone.
 */
export function workoutEndMs(signal) {
    const minutes = workoutMinutes(signal.payload);
    return occurredAtMs(signal) + (minutes ?? 0) * 60_000;
}
/**
 * The most recent strength `wearable.workout` on the given local day, or null.
 * Used to credit a session against a lower step count for the whole day, not
 * only inside a post-session recovery window.
 */
export function latestStrengthWorkoutOnDay(signals, now, timezone) {
    const today = dayKey(now.getTime(), timezone);
    let best = null;
    let bestAt = 0;
    for (const signal of signals) {
        if (signal.kind !== "wearable.workout")
            continue;
        if (!classifyWorkout(signal.payload).isStrength)
            continue;
        const at = occurredAtMs(signal);
        if (at <= bestAt || dayKey(at, timezone) !== today)
            continue;
        best = signal;
        bestAt = at;
    }
    return best;
}
//# sourceMappingURL=workout.js.map